import { Fragment, useState, type ReactNode } from "react";
import {
  ChevronRight,
  ListChecks,
  MessageCircle,
  Package,
  type LucideIcon,
} from "lucide-react";
import { track } from "@/lib/analytics";
import { companyThemeClassName } from "./company-theme";
import { InquiryShell } from "./InquiryShell";
import type { InquiryProfileConfig } from "./inquiry-profile-schema";
import { type DryHireDraft, type QuickInquiryDraft } from "./submit-inquiry";
import { DryHireForm } from "./DryHireForm";
import { QuickInquiryForm } from "./QuickInquiryForm";
import { SoundgearInquiry } from "./SoundgearInquiry";
import styles from "./InquiryFlow.module.css";

type InquiryPath = "quick_enquiry" | "full_brief" | "dry_hire" | null;

/**
 * Full-width choice row: icon, label, one-line benefit, chevron. Raw
 * <button>, not the Button component — the bold/padded button styling is
 * wrong for a two-line choice row (same exception DESIGN.md grants option
 * rows). `primary` tints the icon with the brand accent to mark the
 * recommended path.
 */
function PathChoiceRow({
  icon: Icon,
  label,
  description,
  primary = false,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  description: string;
  primary?: boolean;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={styles.choice}
    >
      <span
        className={`${styles.choiceIcon} ${
          primary ? styles.choiceIconPrimary : ""
        }`}
      >
        <Icon className={styles.glyph} />
      </span>
      <span className={styles.choiceText}>
        <span className={styles.choiceLabel}>
          {label}
        </span>
        <span className={styles.choiceDescription}>{description}</span>
      </span>
      <ChevronRight className={styles.chevron} />
    </button>
  );
}

/**
 * A published call-in number, grouped for reading. Only the two calling
 * codes the product provisions are grouped; anything else renders as the
 * stored E.164, which is dialable and unambiguous even when unpretty.
 */
export function formatCallNumber(e164: string): string {
  if (/^\+64\d{8,9}$/.test(e164)) {
    const rest = e164.slice(3);
    // Mobiles are 02x; landlines carry a single-digit area code.
    const area = rest.startsWith("2") ? rest.slice(0, 2) : rest.slice(0, 1);
    const local = rest.slice(area.length);
    return `+64 ${area} ${local.slice(0, 3)} ${local.slice(3)}`;
  }
  if (/^\+1\d{10}$/.test(e164)) {
    const rest = e164.slice(2);
    return `+1 ${rest.slice(0, 3)} ${rest.slice(3, 6)} ${rest.slice(6)}`;
  }
  return e164;
}

export function InquiryFlow({
  profile,
}: {
  profile: InquiryProfileConfig;
}): React.JSX.Element {
  const [path, setPathState] = useState<InquiryPath>(null);
  const setPath = (next: InquiryPath): void => {
    setPathState(next);
    if (next) track("path_chosen", { profileKey: profile.key, path: next });
  };
  const [quickDraft, setQuickDraft] = useState<QuickInquiryDraft>(() => ({
    contactName: "",
    requestSummary: "",
    contactEmail: "",
    eventName: "",
    eventDate: "",
    location: "",
  }));
  const [dryHireDraft, setDryHireDraft] = useState<DryHireDraft>(() => ({
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    startDate: "",
    endDate: "",
    lines: [],
  }));

  return (
    // One scope for the Company's colours over every path and the
    // confirmation after it (company-theme.ts) — the chooser, the quick
    // form, the wizard and the catalogue are one form in one Company's
    // colours, so the scope is above all of them rather than in each.
    <div className={companyThemeClassName(profile.company?.theme)}>
      <div hidden={path !== null}>
        <InquiryShell
          company={
            profile.company
              ? { name: profile.company.name, url: profile.company.publicUrl }
              : undefined
          }
        >
          <div className={styles.entry}>
            <div className={styles.entryBody}>
              <h1 className={styles.heading}>
                {profile.entryFlow.title}
              </h1>
              <p className={styles.subheading}>
                {profile.entryFlow.description}
              </p>
              {/* The paths are alternatives, so an "or" sits between each
                  pair: a Customer reading down the page shouldn't think they
                  have to do every one. */}
              <div className={styles.choices}>
                {withOr([
                  profile.entryFlow.fullBrief.enabled && (
                    <PathChoiceRow
                      key="full_brief"
                      icon={ListChecks}
                      label={profile.entryFlow.fullBrief.label}
                      description={profile.entryFlow.fullBrief.description}
                      primary
                      onClick={() => setPath("full_brief")}
                    />
                  ),
                  profile.entryFlow.quickEnquiry.enabled && (
                    <PathChoiceRow
                      key="quick_enquiry"
                      icon={MessageCircle}
                      label={profile.entryFlow.quickEnquiry.label}
                      description={profile.entryFlow.quickEnquiry.description}
                      onClick={() => setPath("quick_enquiry")}
                    />
                  ),
                  profile.entryFlow.dryHire.enabled && (
                    <PathChoiceRow
                      key="dry_hire"
                      icon={Package}
                      label={profile.entryFlow.dryHire.label}
                      description={profile.entryFlow.dryHire.description}
                      onClick={() => setPath("dry_hire")}
                    />
                  ),
                ])}
              </div>
              {/* Below the paths and deliberately quiet: the choice rows are
                  what this page offers, and the phone is the way out for the
                  Customer who would rather not fill in any of them. A fourth
                  row would read as a fourth form. */}
              {profile.phone && (
                <p className={styles.phoneLine}>
                  Rather talk to someone?{" "}
                  <a
                    href={`tel:${profile.phone.e164}`}
                    className={styles.phoneLink}
                    onClick={() =>
                      track("path_chosen", {
                        profileKey: profile.key,
                        path: "call",
                      })
                    }
                  >
                    Call {formatCallNumber(profile.phone.e164)}
                  </a>
                </p>
              )}
            </div>
          </div>
        </InquiryShell>
      </div>

      <div hidden={path !== "quick_enquiry"}>
        <QuickInquiryForm
          profile={profile}
          draft={quickDraft}
          onDraftChange={setQuickDraft}
          onBack={() => setPath(null)}
        />
      </div>

      <div hidden={path !== "full_brief"}>
        <SoundgearInquiry
          profile={profile}
          onBackToEntry={() => setPath(null)}
        />
      </div>

      {/* Mount-gated on the flag (unlike the siblings): mounting fires the
          catalogue fetch, which would hit the public endpoint for every
          visitor of a Company that never enabled dry hire. When enabled,
          mounting early is deliberate — the catalogue prefetches while the
          Customer reads the chooser. */}
      {profile.entryFlow.dryHire.enabled && (
        <div hidden={path !== "dry_hire"}>
          <DryHireForm
            profile={profile}
            draft={dryHireDraft}
            onDraftChange={setDryHireDraft}
            onBack={() => setPath(null)}
          />
        </div>
      )}
    </div>
  );
}

/** The enabled path rows, with an "or" between each pair. */
function withOr(rows: (ReactNode | false)[]): ReactNode[] {
  return rows
    .filter((row): row is ReactNode => Boolean(row))
    .map((row, i) => (
      <Fragment key={i}>
        {i > 0 && (
          <p className={styles.or} aria-hidden="true">
            or
          </p>
        )}
        {row}
      </Fragment>
    ));
}
